import type { ValidationError } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useGetPatientWithId, useUpdatePatient } from "@/api/patients";
import ChevronRight from "@/assets/icons/chevron-right";
import { FormBlocker } from "@/components/form/form-blocker";
import UpdateForm from "@/components/form/patient/update-form";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { emailSchema } from "@/schemas/auth";
import type { UpdatePatientFormData } from "@/schemas/patient";
import {
  updatePatientDefaultValues,
  updatePatientSchema,
} from "@/schemas/patient";
import { resetForm } from "@/utils/form";

const PatientFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useGetPatientWithId(location.state.patientId);
  const { mutate } = useUpdatePatient(location.state.patientId);

  const form = useForm({
    defaultValues: {
      ...updatePatientDefaultValues,
      ...data,
      birthDate: data
        ? new Date(data.birthDate)
        : updatePatientDefaultValues.birthDate,
      id: undefined,
      doctorId: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    } as UpdatePatientFormData,
    validators: {
      onSubmit: ({ value }) => {
        const { postalCode, lastName, firstName, birthDate, email } = value;
        const returnValue: { fields: { [k: string]: ValidationError } } = {
          fields: {},
        };
        if (postalCode && (postalCode < 10000 || postalCode > 99999)) {
          const postalCodeError: ValidationError =
            "Le code postal doit contenir 5 chiffres";
          returnValue.fields["postalCode"] = postalCodeError;
        }
        if (!emailSchema.safeParse(email).success) {
          const emailError: ValidationError = "Veuillez entrer un email valide";
          returnValue.fields["email"] = emailError;
        }
        if (!birthDate) {
          const birthDateError: ValidationError =
            "Veuillez entrer une date de naissance";
          returnValue.fields["birthDate"] = birthDateError;
        }
        if (!lastName) {
          const lastNameError: ValidationError = "Le nom est obligatoire";
          returnValue.fields["lastName"] = lastNameError;
        }
        if (!firstName) {
          const firstNameError: ValidationError = "Le prénom est obligatoire";
          returnValue.fields["firstName"] = firstNameError;
        }
        return returnValue;
      },
    },
    onSubmit: async ({ value, formApi }) => {
      const values = updatePatientSchema.parse(value);
      if (values.allergies === null) {
        values.allergies = [];
      }
      if (values.associatedPathologies === null) {
        values.associatedPathologies = [];
      }
      if (values.womenHealth === null) {
        values.womenHealth = [];
      }
      mutate(values, {
        onSuccess: () => {
          resetForm(values, formApi);
          navigate(-1);
        },
      });
    },
  });

  const { handleSubmit, Subscribe } = form;

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col w-full gap-6 px-4 sm:px-6 lg:px-[5%]"
    >
      <div className="flex flex-col gap-6">
        <div className="flex gap-2 items-center">
          <Link to={"/patients"}>
            <span className="text-gray-600">{"Patients"}</span>
          </Link>
          <ChevronRight />
          <span className="text-brand-700 font-semibold">
            {"Ajouter un patient"}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h2 className="font-semibold text-2xl sm:text-[32px] flex-shrink-0 min-w-0">
            {"Ajouter un patient"}
          </h2>
          <div className="flex gap-4 flex-shrink-0 min-w-0">
            <Link
              to={"/patients"}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "flex-1 sm:flex-initial sm:w-auto whitespace-nowrap"
              )}
            >
              {"Annuler"}
            </Link>
            <Button
              type={"button"}
              variant={"blue"}
              onClick={handleSubmit}
              className="flex-1 sm:flex-initial sm:w-auto whitespace-nowrap"
            >
              {"Enregistrer"}
            </Button>
          </div>
        </div>
      </div>
      <UpdateForm formApi={form} />
      <FormBlocker Subscribe={Subscribe} />
    </form>
  );
};

export default PatientFormPage;
